import Cookies from "js-cookie";
import useSWR from "swr";

import {ApiError, apiFetcher, friendlyErrorMessage, unwrapEnvelope} from "@lib/fetcher";

import type {CashFlowMonth, CashFlowSection} from "./use-cashflow-statement";

// ── Types ─────────────────────────────────────────────────────

export type {CashFlowMonth, CashFlowSection};

export interface CashFlowScenario {
    id: string;
    name: string;
    domain: string;
    is_default: boolean;
    baseline_method: string;
    created_at: string;
    updated_at: string;
    ops_count?: number;
}

export interface CashFlowCellProvenance {
    value: string;
    baseline: string;
    is_override: boolean;
    ops_applied: {
        op_id: string;
        label: string;
        op_type: string;
        driver_type?: string;
        value: string;
        effect: string;
    }[];
    calculation: string;
}

export interface CashFlowV2Data {
    connected: boolean;
    message?: string;
    scenario: CashFlowScenario;
    baseline: {totalInflow: number; totalOutflow: number};
    months: CashFlowMonth[];
    cell_provenance: Record<string, CashFlowCellProvenance>;
    ops_count: number;
}

export interface CashFlowOp {
    id: string;
    op_type: "set_baseline" | "set_driver" | "set_override";
    target_type: "cashflow_category" | "cashflow_cell";
    target_id: string;
    label: string;
    params: Record<string, unknown>;
    ordinal: number;
    is_active: boolean;
    created_by: "human" | "agent";
    created_at: string;
}

interface SummaryDelta {
    before: string;
    after: string;
    delta: string;
}

interface CellDelta {
    cell: string;
    row_name: string;
    month_label: string;
    before: string;
    after: string;
    delta: string;
    delta_pct: string | null;
}

export interface CashFlowPreviewResult {
    deltas: CellDelta[];
    summary_deltas: Record<string, SummaryDelta>;
    after: {
        months: CashFlowMonth[];
        baseline: {totalInflow: number; totalOutflow: number};
        cell_provenance: Record<string, CashFlowCellProvenance>;
    };
}

export interface CashFlowOpInput {
    op_type: "set_baseline" | "set_driver" | "set_override";
    target_type: "cashflow_category" | "cashflow_cell";
    target_id: string;
    label?: string;
    params: Record<string, unknown>;
}

// ── Fetcher ────────────────────────────────────────────────────
// GET requests use the shared apiFetcher (timeout + retry + envelope unwrap).

const MUTATION_TIMEOUT_MS = 60_000;

const postFetcher = async <T>(url: string, body: unknown): Promise<T> => {
    const csrfToken = Cookies.get("csrftoken") || "";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(body),
            signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) {
            let msg: string | undefined;
            try {
                const data = await response.json();
                msg = (data.errors as string[])?.[0] || (data.error as string) || undefined;
            } catch { /* ignore */ }
            const kind = response.status === 401 ? "auth"
                : response.status === 403 ? "permission"
                : response.status >= 500 ? "server" : "unknown";
            if (response.status === 401) msg = "Session expired — please log in again.";
            if (response.status === 403) msg = "Permission denied — your Xero connection may have expired.";
            throw new ApiError(msg || `Request failed (${response.status})`, kind, response.status);
        }
        const json = await response.json();
        return unwrapEnvelope<T>(json);
    } catch (err) {
        clearTimeout(timer);
        if (err instanceof ApiError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
            throw new ApiError("Request timed out — please try again.", "network");
        }
        throw new ApiError("Network error — check your connection and try again.", "network");
    }
};

// ── SWR Hooks ──────────────────────────────────────────────────

export function useCashFlowV2(scenarioId?: string, months = 6) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = `/api/cashflow/v2/?${params}`;

    const {data, error, isLoading, isValidating, mutate} = useSWR<CashFlowV2Data>(url, apiFetcher, {
        
        dedupingInterval: 60000,
        errorRetryCount: 3,
        keepPreviousData: true,
    });

    const errorKind = error instanceof ApiError ? error.kind : error ? "unknown" as const : null;
    const showError = error && !(isValidating && data);

    return {
        data,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        errorKind: showError ? errorKind : null,
        isConnected: data?.connected ?? false,
        refresh: mutate,
    };
}

export function useCashFlowOps(scenarioId?: string) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = `/api/cashflow/v2/ops/?${params}`;

    const {data, error, isLoading, mutate} = useSWR<{scenario_id: string; ops: CashFlowOp[]}>(url, apiFetcher, {
        dedupingInterval: 10000,
    });

    return {ops: data?.ops ?? [], isLoading, error, refresh: mutate};
}

export function useCashFlowScenarios() {
    const {data, error, isLoading, mutate} = useSWR<{scenarios: CashFlowScenario[]}>(
        "/api/cashflow/v2/scenarios/", apiFetcher, {dedupingInterval: 30000}
    );
    return {scenarios: data?.scenarios ?? [], isLoading, error, refresh: mutate};
}

export function useCashFlowBaselineMethods() {
    // Cashflow uses the same baseline methods as P&L
    const {data} = useSWR<{methods: {key: string; label: string; trailing: number}[]}>(
        "/api/forecast/v2/baseline-methods/", apiFetcher, {dedupingInterval: 300000}
    );
    return data?.methods ?? [];
}

export async function createCashFlowScenario(
    name: string,
    baselineMethod = "avg_3m",
    copyFrom?: string,
): Promise<CashFlowScenario> {
    return postFetcher<CashFlowScenario>("/api/cashflow/v2/scenarios/", {
        name,
        baseline_method: baselineMethod,
        copy_from: copyFrom,
    });
}

// ── Mutations ──────────────────────────────────────────────────

export async function previewCashFlowOps(
    ops: CashFlowOpInput[],
    scenarioId?: string,
    months = 6,
): Promise<CashFlowPreviewResult> {
    return postFetcher<CashFlowPreviewResult>("/api/cashflow/v2/preview/", {
        ops,
        scenario_id: scenarioId,
        months,
    });
}

export async function commitCashFlowOps(
    ops: CashFlowOpInput[],
    scenarioId?: string,
    months = 6,
) {
    return postFetcher<{
        committed_ops: CashFlowOp[];
        scenario: CashFlowScenario;
        months: CashFlowMonth[];
    }>("/api/cashflow/v2/commit/", {
        ops,
        scenario_id: scenarioId,
        months,
        created_by: "human",
    });
}

export async function deleteCashFlowOp(opId: string): Promise<void> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
    try {
        const response = await fetch(`/api/cashflow/v2/ops/${opId}/`, {
            method: "DELETE",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) {
            const kind = response.status === 401 ? "auth"
                : response.status === 403 ? "permission"
                : response.status >= 500 ? "server" : "unknown";
            throw new ApiError(`Failed to delete op (${response.status})`, kind, response.status);
        }
    } catch (err) {
        clearTimeout(timer);
        if (err instanceof ApiError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
            throw new ApiError("Request timed out — please try again.", "network");
        }
        throw new ApiError("Network error — check your connection and try again.", "network");
    }
}
